import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { snakeCase } from 'lodash';
import humanizeString from 'humanize-string';

import { databaseDescriptionShape } from '../../../../../propsShape';

import { DataDictionarySourceToggle } from '../../molecules';
import { Text, Icon, H4, Input } from '../../../../../components';

const SchemasAccordionMenuContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  overflow-y: auto;
  gap: 12px;
  width: fit-content;
  max-width: 370px;
  min-width: 250px;
`;

const TableContainer = styled.div`
  display: flex;
  align-items: center;
  padding-left: 26px;
  gap: 4px;
  margin-bottom: 2px;
  margin-right: ${({ $selected }) => ($selected ? '16px' : '0')};
  background-color: ${({ $selected }) => ($selected ? 'var(--wash-gray-pressed)' : 'transparent')};
  &:hover {
    background-color: var(--wash-gray-hover);
    margin-right: 16px;
  }
  border-radius: 4px;
`;

const TableName = styled(Text)`
  padding: 6px 12px 6px 0;
  cursor: pointer;
  width: 100%;
  color: var(--content-title);
`;

const SearchContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
`;

const SearchInput = styled(Input)`
  width: calc(100% - 16px);
  height: 40px;
  background: var(--wash-gray-default);
  padding: 4px 35px;
  border-radius: 8px;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const SearchInputIcon = styled(Icon)`
  position: absolute;
  top: 10px;
  left: 10px;
`;

const NoResultsContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px;
  color: var(--content-subtitle);
`;

const SchemasAccordionMenu = ({
  schemas,
  selectedTable,
  setSelectedTable,
  setSelectedSchema,
  getSourceName,
  getSourcePicture,
  className,
  isSnakeCase = false,
  withTableSearch = false
}) => {
  // State to track which source is open. Default to the first one (if any)
  const [openSource, setOpenSource] = useState(schemas[0]?.id);
  const [tableFilterText, setTableFilterText] = useState('');

  const formatTableName = useCallback((tableName) => tableName.split('.')[1], []);

  const handleSelectTable = useCallback((table) => {
    setSelectedTable(table);
    setSelectedSchema?.(null);
  }, []);

  const handleSelectSchema = useCallback(
    (schema) => {
      if (setSelectedSchema && setSelectedTable) {
        setSelectedSchema(schema.name);
        setSelectedTable(null);
        setOpenSource(schema.id);
      } else {
        setOpenSource(openSource === schema.id ? null : schema.id);
      }
    },
    [openSource, setSelectedSchema, setSelectedTable]
  );

  const handleTableFilterChange = useCallback((e) => {
    setTableFilterText(e.target.value);
  }, []);

  // Filter schemas and views based on search text
  const filteredSchemas = useMemo(() => {
    if (!tableFilterText.trim() || !schemas) return schemas;

    return schemas
      .map((schema) => {
        const lowerCaseFilter = tableFilterText.toLowerCase();
        // Filter views that match the search term
        const filteredViews = {};

        Object.entries(schema.views || {}).forEach(([key, view]) => {
          const viewName = view.name.split('.')?.[1]?.toLowerCase() || view.name.toLowerCase();

          if (viewName.startsWith(lowerCaseFilter)) {
            filteredViews[key] = view;
          }
        });

        // Return schema with filtered views if any match found
        if (Object.keys(filteredViews).length > 0) {
          return {
            ...schema,
            views: filteredViews
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [schemas, tableFilterText]);

  const schemasToRender = withTableSearch ? filteredSchemas : schemas;
  const hasNoResults = withTableSearch && tableFilterText.trim() && schemasToRender.length === 0;

  return (
    <SchemasAccordionMenuContainer className={className}>
      {withTableSearch && (
        <SearchContainer>
          <SearchInputIcon name='search' displaySize={20} color='var(--content-subtitle)' />
          <SearchInput
            type='text'
            placeholder='Search for tables...'
            onChange={handleTableFilterChange}
            value={tableFilterText}
          />
        </SearchContainer>
      )}
      <H4 color='var(--content-subtitle)'>Sources</H4>
      {hasNoResults ? (
        <NoResultsContainer>
          <Text>No tables found</Text>
        </NoResultsContainer>
      ) : (
        schemasToRender.map((schema) => (
          <DataDictionarySourceToggle
            key={schema.id}
            name={getSourceName(schema.name)}
            picture={getSourcePicture(schema.name)}
            isOpen={openSource === schema.id}
            onToggle={() => setOpenSource(openSource === schema.id ? null : schema.id)}
            handleSelectSchema={() => handleSelectSchema(schema)}
          >
            {Object.keys(schema.views).map((view) => (
              <TableContainer key={view} $selected={selectedTable === view}>
                <Icon
                  size={24}
                  displaySize={18}
                  name='table'
                  color='var(--action-tertiary-default)'
                />
                <TableName onClick={() => handleSelectTable(view)}>
                  {isSnakeCase
                    ? snakeCase(formatTableName(view))
                    : humanizeString(formatTableName(view))}
                </TableName>
              </TableContainer>
            ))}
          </DataDictionarySourceToggle>
        ))
      )}
    </SchemasAccordionMenuContainer>
  );
};

SchemasAccordionMenu.propTypes = {
  schemas: databaseDescriptionShape.isRequired,
  selectedTable: PropTypes.string.isRequired,
  setSelectedTable: PropTypes.func.isRequired,
  isSnakeCase: PropTypes.bool,
  setSelectedSchema: PropTypes.func.isRequired,
  getSourceName: PropTypes.func.isRequired,
  getSourcePicture: PropTypes.func.isRequired,
  withTableSearch: PropTypes.bool,
  className: PropTypes.string
};

export { SchemasAccordionMenu };
